import { APP_CONSTANTS } from 'constants/index';
import { generateSecretKey, parseChannelData } from '../../../common/utils/decrypt';
import { CHANNEL_MESSAGE_TYPES } from '../../../constants/common';

/**
 * Gets the active view configuration from localStorage
 * @returns {Object|null} The active view configuration with filters, sorts, and columns, or null if not found
 */
export const getActiveViewFromStorage = () => {
  const key = APP_CONSTANTS.PERSIST_QUEUE;
  const jsonString = localStorage.getItem(key);

  if (!jsonString) {
    console.warn(`Websockets: No data found for key "${key}" in localStorage`);
    return null;
  }

  try {
    const viewData = JSON.parse(jsonString);
    const activeView = JSON.parse(viewData.activeView);
    return {
      filters: activeView.columnsConfig.customFilterModel,
      sorts: activeView.columnsConfig.customSortModel,
      columns: activeView.columnsConfig.columnsOrder,
    };
  } catch (error) {
    console.error('Websockets: Error parsing JSON:', error);
    return null;
  }
};

/**
 * Checks if field is in columns list
 * @param {Array} params - Array of parameters to check
 * @param {Array} columns - Array of column configurations
 * @returns {boolean} True if field is found in columns list
 */
export const isFieldInColumnsList = (params, columns) => {
  for (const element of columns) {
    const field = element.fieldName;
    if (params.includes(field)) {
      return true;
    }
  }
  return false;
};

/**
 * Checks if field is used in filters
 * @param {Array} params - Array of parameters to check
 * @param {Array} filters - Array of filter configurations
 * @returns {boolean} True if field is used in filters
 */
export const isFieldUsedInFilters = (params, filters) => {
  for (const element of filters) {
    const field = element.field;
    if (params.includes(field)) {
      return true;
    }
  }
  return false;
};

/**
 * Checks if field is used in sorts
 * @param {Array} params - Array of parameters to check
 * @param {Array} sorts - Array of sort configurations
 * @returns {boolean} True if field is used in sorts
 */
export const isFieldUsedInSorts = (params, sorts) => {
  for (const element of sorts) {
    const field = element.colId;
    if (params.includes(field)) {
      return true;
    }
  }
  return false;
};

/**
 * Checks if filters contain updated parameters
 * @param {Array} params - Array of parameters to check
 * @returns {Object} Object containing isInQueueColumsList and isUsedInFilters flags
 */
export const isFiltersContainsUpdatedParams = (params) => {
  let isInQueueColumsList = false;
  let isUsedInFilters = false;

  try {
    const viewConfig = getActiveViewFromStorage();
    if (!viewConfig) {
      return { isInQueueColumsList: true, isUsedInFilters: false };
    }

    const { columns, filters, sorts } = viewConfig;

    // Check if field is in columns configuration
    isInQueueColumsList = isFieldInColumnsList(params, columns);

    // If field not in columns, no need to check filters/sorts
    if (isInQueueColumsList) {
      // Check filters first
      isUsedInFilters = isFieldUsedInFilters(params, filters);

      // If not found in filters, check sorts
      if (!isUsedInFilters) {
        isUsedInFilters = isFieldUsedInSorts(params, sorts);
      }
    }
  } catch (error) {
    console.error('Websockets: Error while checking params in queue configuration', error);
  }

  return {
    isInQueueColumsList: true,
    isUsedInFilters,
  };
};

/**
 * Gets empty field values
 * @param {Object} field - The field object
 * @param {Object} newValues - New values object
 * @param {string} fieldName - Name of the field
 * @returns {Object} Object with value and categoryCaption
 */
export const getEmptyFieldValues = (field, newValues, fieldName) => ({
  value: newValues?.[fieldName] || field?.value,
  categoryCaption: newValues?.[fieldName] === '-1' ? '' : field.categoryCaption,
});

/**
 * Handles assignee field updates
 * @param {Object} field - The field object to update
 * @param {Object} updatedParams - Updated parameters containing assignee data
 * @returns {Object} Updated field object with assignee information
 */
export const updateAssigneeField = (field, updatedParams) => ({
  ...field,
  value: {
    assignee: updatedParams.assignee,
    assignedGroup: updatedParams.assignedGroup,
  },
});

/**
 * Handles category field updates
 * @param {Object} field - The field object to update
 * @param {Object} newValues - New values object
 * @param {string} fieldName - Name of the field
 * @param {Function} invalidateQueryData - Function to invalidate query data
 * @returns {Object} Updated field object
 */
export const updateCategoryField = (field, newValues, fieldName, invalidateQueryData) => {
  invalidateQueryData();
  return {
    ...field,
    ...getEmptyFieldValues(field, newValues, fieldName),
  };
};

/**
 * Handles default field updates
 * @param {Object} field - The field object to update
 * @param {Object} updatedParams - Updated parameters
 * @param {string} fieldName - Name of the field
 * @returns {Object} Updated field object
 */
export const updateDefaultField = (field, updatedParams, fieldName) => {
  const prevValue = field?.value;
  return {
    ...field,
    value: fieldName in updatedParams ? updatedParams[fieldName] : prevValue,
  };
};

/**
 * Creates update state for add ticket events
 * @param {boolean} isEnabled - Whether updates are enabled
 * @param {Object} existingSrs - Existing service requests
 * @param {string} name - Event name
 * @param {Object} data - Event data
 * @returns {Object} Update state object
 */
export const createAddTicketUpdate = (isEnabled, existingSrs, name, data) => ({
  isEnabled,
  existingSrs,
  updates: new Map().set(CHANNEL_MESSAGE_TYPES.ADD_TICKET, { id: null, name, data }),
});

/**
 * Creates regular update state
 * @param {Object} prev - Previous state
 * @param {string|number} id - ID of the item
 * @param {string} name - Event name
 * @param {Object} data - Event data
 * @param {string} mapkey - Map key for the update
 * @param {Object} parsedData - Parsed data
 * @returns {Object} Updated state object
 */
export const createRegularUpdate = (prev, id, name, data, mapkey, parsedData) => {
  const { existingSrs, isEnabled, updates } = prev;
  return {
    existingSrs,
    isEnabled,
    updates: new Map(updates.set(mapkey, { id, name, data: parsedData })),
  };
};

/**
 * Parses channel data safely with error handling
 * @param {Object} data - Data to parse
 * @param {string} name - Channel name
 * @param {string} secretKey - Secret key for decryption
 * @param {Object} prevData - Previous data
 * @returns {Object} Parsed data or original data if parsing fails
 */
export const parseChannelDataSafely = (data, name, secretKey, prevData) => {
  try {
    return parseChannelData({
      data,
      name,
      secretKey,
      prevData,
    });
  } catch (error) {
    console.error('Websockets: Error parsing channel data', error);
    return data;
  }
};

/**
 * Main utility function to combine and decrypt update state
 * @param {Object} params - Parameters object
 * @param {Object} params.prev - Previous state
 * @param {Object} params.data - Data to process
 * @param {string} params.name - Event name
 * @returns {Object} Combined and decrypted update state
 */
export const combineAndDecyrptUpdateState = ({ prev, data, name }) => {
  try {
    const { existingSrs, isEnabled } = prev;

    if (name === CHANNEL_MESSAGE_TYPES.ADD_TICKET) {
      return createAddTicketUpdate(isEnabled, existingSrs, name, data);
    }

    const id = data?.srId;
    if (existingSrs?.[id]) {
      const mapkey = id + name;
      const secretKey = generateSecretKey({ id, insertTime: existingSrs[id] });
      const parsedData = parseChannelDataSafely(data, name, secretKey, prev.updates.get(mapkey)?.data);

      return createRegularUpdate(prev, id, name, data, mapkey, parsedData);
    }

    return prev;
  } catch (error) {
    console.error('Websockets: combineAndDescyrptUpdateState', error);
    return prev;
  }
};

/**
 * Checks if SR should be updated
 * @param {Array} sr - Service request array
 * @param {string|number} id - ID to check
 * @returns {Object|undefined} Found service request or undefined
 */
export const shouldUpdateSR = (sr, id) => sr.find((col) => col.field === 'id' && col.value === Number(id));

/**
 * Updates service request counts
 * @param {Object} prevQueue - Previous queue state
 * @returns {Object} Updated counts object
 */
export const updateServiceRequestCounts = (prevQueue) => ({
  count: prevQueue.count - 1,
  countTotal: prevQueue.countTotal - 1,
  srTypeFilterCounterAll: prevQueue.srTypeFilterCounterAll - 1,
});

/**
 * Creates lock status data
 * @param {boolean} isLocked - Whether the item is locked
 * @param {string|null} userName - Name of the locking user
 * @returns {Object} Lock status object
 */
export const createLockStatusData = (isLocked, userName = null) => ({
  isLocked,
  lockingUser: userName,
});

/**
 * Removes update from queue state
 * @param {Object} prev - Previous state
 * @param {string} key - Key to remove
 * @returns {Object} Updated state with removed update
 */
export const removeUpdateFromQueue = (prev, key) => {
  if (prev.updates.has(key)) {
    prev.updates.delete(key);
  }
  return { ...prev, updates: new Map(prev.updates) };
};

/**
 * Processes a single bulk item
 * @param {Object} item - Item object with key and value
 * @param {Function} setQueueUpdates - Function to set queue updates
 * @param {Function} updateCacheByType - Function to update cache by type
 * @param {boolean} isNotUmounted - Whether component is not unmounted
 * @returns {*} Result of updateCacheByType or null
 */
export const processBulkItem = ({ key, value }, setQueueUpdates, updateCacheByType, isNotUmounted) => {
  if (!isNotUmounted) return null;

  const { id, name, data } = value;
  setQueueUpdates((prev) => removeUpdateFromQueue(prev, key));
  return updateCacheByType({ name, data, id });
};

/**
 * Creates bulk items array from updates list
 * @param {Map} updatesList - Map of updates
 * @returns {Array} Array of bulk items with key and value
 */
export const createBulkItemsArray = (updatesList) => Array.from(updatesList, ([key, value]) => ({ key, value }));
